import type { LpExpressRun } from "@shared/schema";
import {
  LEGACY_COMPOSITE_EXPANSIONS,
  LP_SECTION_TYPES,
  type LpSectionType,
} from "@shared/lp-express-sections";

/** Shared key for every generated-content save. Publish waits for this key. */
export const LP_PENDING_SAVE_KEY = ["lp-express-pending-save"] as const;

export type LpSectionLayoutItem = {
  type: LpSectionType;
  hidden: boolean;
};

export function canonicalSectionLayout(
  types: readonly LpSectionType[],
): LpSectionLayoutItem[] {
  return types.map((type) => ({ type, hidden: false }));
}

/**
 * The persisted sections list is authoritative. Missing entries stay deleted;
 * only a missing/unusable list falls back to the template's canonical layout.
 */
export function sectionLayoutFromSections(
  sections: unknown,
  fallbackTypes: readonly LpSectionType[],
): LpSectionLayoutItem[] {
  if (!Array.isArray(sections) || sections.length === 0) {
    return canonicalSectionLayout(fallbackTypes);
  }

  const known = new Set<string>(LP_SECTION_TYPES as readonly string[]);
  const seen = new Set<string>();
  const result: LpSectionLayoutItem[] = [];

  for (const entry of sections) {
    const type = typeof entry === "string" ? entry : (entry as { type?: unknown } | null)?.type;
    if (typeof type !== "string") continue;

    const expansion = LEGACY_COMPOSITE_EXPANSIONS[type];
    if (expansion) {
      for (const expandedType of expansion) {
        if (seen.has(expandedType)) continue;
        seen.add(expandedType);
        result.push({ type: expandedType, hidden: false });
      }
      continue;
    }

    if (!known.has(type) || seen.has(type)) continue;
    seen.add(type);
    result.push({
      type: type as LpSectionType,
      hidden: typeof entry === "object" && entry !== null
        ? Boolean((entry as { hidden?: unknown }).hidden)
        : false,
    });
  }

  return result.length > 0 ? result : canonicalSectionLayout(fallbackTypes);
}

export function sameSectionLayout(
  first: readonly LpSectionLayoutItem[],
  second: readonly LpSectionLayoutItem[],
): boolean {
  return first.length === second.length
    && first.every((section, index) =>
      section.type === second[index].type
      && Boolean(section.hidden) === Boolean(second[index].hidden));
}

export type OriginCopyState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  fresh_badge?: string;
  block_1_headline?: string;
  block_1_body?: string;
  block_2_headline?: string;
  block_2_body?: string;
  block_3_headline?: string;
  block_3_body?: string;
  location_carousel_captions?: string[];
  origin_carousel_captions?: string[];
};

export type QuoteState = { quote?: string; attribution?: string };
export type LpImageSlot = {
  key?: string;
  imageUrl?: string;
  altText?: string;
  imageStatus?: string;
};

export type VillainState = {
  checklist_headline?: string;
  checklist_items?: string[];
  checklist_footer?: string;
  report_eyebrow?: string;
  report_headline?: string;
  report_rows?: Array<{ name?: string; note?: string; grade?: string }>;
  belief_eyebrow?: string;
  belief_headline?: string;
};

export type StationsState = {
  section_label?: string;
  section_headline?: string;
  stations?: Array<{ num?: number | string; label?: string; sub?: string }>;
  loop_quote?: string;
};

export type TimelineState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  steps?: Array<{ label?: string; headline?: string; bullets?: string[] }>;
  disclaimer?: string;
  sticky_note_headline?: string;
};

export type MembershipState = {
  section_eyebrow?: string;
  section_headline?: string;
  body_1?: string;
  body_2?: string;
  body_closing?: string;
  membership_label?: string;
  membership_tagline?: string;
  benefits?: Array<{ icon_hint?: string; label?: string; note?: string }>;
  objection_note_1?: string;
  objection_note_2?: string;
  objection_note_3?: string;
};

export type AlternativesState = {
  section_eyebrow?: string;
  hero_headline?: string;
  hero_subheadline?: string;
  hero_differentiators?: string[];
  competitor_intro?: string;
  competitor_cards?: Array<{ title?: string; body?: string }>;
  comparison_them_label?: string;
  comparison_us_label?: string;
  comparison_them?: string[];
  comparison_us?: string[];
  closing_line_1?: string;
  closing_line_2?: string;
};

export type PhilosophyState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_body_1?: string;
  section_body_2?: string;
  pillar_card_label?: string;
  pillars?: Array<{ num?: string; title?: string; body?: string }>;
  pillar_footer_words?: string[];
};

export type DifferentiatorState = {
  section_eyebrow?: string;
  section_headline?: string;
  features?: Array<{ icon_hint?: string; title?: string; body?: string }>;
  trust_badge?: string;
};

export type FaqState = {
  faqs?: Array<{ question?: string; answer?: string }>;
};

export type ReviewsState = {
  reviews?: Array<{
    name?: string;
    variant?: string | null;
    quote?: string;
    verified?: boolean;
  }>;
};

export type FinalCtaState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  bundle_label?: string;
  savings_badge?: string;
  cta_button?: string;
  cta_subtext?: string;
  trust_badges?: Array<{ icon_hint?: string; label?: string }>;
  social_proof_line?: string;
};

export type FooterState = {
  tagline?: string;
  description?: string;
  explore_label?: string;
  support_label?: string;
  explore_links?: Array<{ label?: string }>;
  support_links?: Array<{ label?: string }>;
  copyright?: string;
  bottom_note?: string;
};

export type ProofLink = { label?: string; url?: string };
export type ProofState = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  proof_folder_headline?: string;
  proof_folder_body?: string;
  proof_folder_bullets?: string[];
  proof_cards?: Array<{
    name?: string;
    what_it_is?: string;
    what_it_does?: string;
    why_it_matters?: string;
    research_links?: ProofLink[];
  }>;
  why_it_matters_headline?: string;
  why_it_matters_body?: string;
  why_it_matters_links?: ProofLink[];
  comparison_them?: string[];
  comparison_us?: string[];
  disclaimer?: string;
};

export type RunRow = LpExpressRun & {
  pageId: string | null;
  resolvedProductName?: string | null;
  generatedContent: {
    hero?: {
      label: string;
      headline: string;
      subheadline: string;
      cta_text: string;
    };
    leadin?: string;
    advisory?: Record<string, string | undefined>;
    showcase?: { headline: string; subheadlines?: string[] };
    productContext?: {
      cardHeader?: string;
      cardSubtext?: string;
      imageUrl?: string;
      altText?: string;
      imageStatus?: string;
    };
    stations?: StationsState;
    villain?: VillainState;
    originCopy?: OriginCopyState;
    originImages?: LpImageSlot[];
    quotes?: Record<string, QuoteState>;
    timeline?: TimelineState;
    membership?: MembershipState;
    alternatives?: AlternativesState;
    philosophy?: PhilosophyState;
    differentiator?: DifferentiatorState;
    faq?: FaqState;
    reviews?: ReviewsState;
    finalCta?: FinalCtaState;
    footer?: FooterState;
    proof?: ProofState;
    alternativesImages?: LpImageSlot[];
    philosophyImages?: LpImageSlot[];
    proofImages?: LpImageSlot[];
  } | null;
};

export interface SharedEditorValues {
  checkoutUrl: string;
  slug: string;
  metaPixelEnabled: boolean;
}

export interface EditorCallbackContract {
  onEdit: (key: string, value: string) => void;
  onRegenerate: (key: string, feedback?: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onPublish: () => void;
}